// Tables & charts guide — the Data group under Components → Elements: the
// Table element (DataTable: in-place editing, CSV import, display options) and
// the Graphic element (GraficaD3: two-step data/options settings window).
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const tablesChartsGuide = {
  id: "tables-charts",
  icon: "grid_on",
  keywords: [
    "table",
    "tablo",
    "data table",
    "veri tablosu",
    "data",
    "veri",
    "row",
    "satır",
    "column",
    "sütun",
    "csv",
    "import csv",
    "csv içe aktar",
    "excel",
    "theme",
    "tema",
    "pagination",
    "sayfalama",
    "search",
    "arama",
    "sort",
    "sıralama",
    "transparent",
    "şeffaf",
    "chart",
    "grafik",
    "graphic",
    "graph",
    "line chart",
    "çizgi grafik",
    "area chart",
    "alan grafiği",
    "bar chart",
    "çubuk grafik",
    "tabla",
    "tabla de datos",
    "datos",
    "fila",
    "columna",
    "importar csv",
    "paginación",
    "ordenar",
    "gráfico",
    "gráfico de líneas",
    "gráfico de barras",
  ],
  content: {
    en: {
      feature: "Components",
      title: "Tables & Charts",
      sections: [
        {
          id: "overview",
          title: "Adding a table or chart",
          videoUrl: null,
          text:
            "Open **Components → Elements** in the left panel. The **Data** group holds two elements — click one to add it to the scene, or drag it onto the canvas:\n\n" +
            "- **Table** — a data table you type into directly on the canvas.\n" +
            "- **Graphic** — a line, area or bar chart built from a small data table.\n\n" +
            "Both start with sample data so you can see the layout right away.",
        },
        {
          id: "editing-tables",
          title: "Editing a table",
          videoUrl: null,
          text:
            "A new table opens straight in edit mode. Later, **double-click** the table to edit it again.\n\n" +
            "- Click any cell — including the header row — and type. **Enter** moves to the cell below, **Shift+Enter** to the cell above.\n" +
            "- **Add row** and **Add column** extend the table; the remove buttons next to each row and column delete it (one row and one column always stay).\n" +
            "- **Import CSV** replaces the table with a CSV file. The first line becomes the header row; comma- and semicolon-separated files (such as Excel exports) both work.\n" +
            "- Click **Done**, press **Esc** or click outside the table to finish. Changes can be undone like any other edit.",
        },
        {
          id: "table-settings",
          title: "Table options and style",
          videoUrl: null,
          text:
            "Select the table and open **Settings** in the right panel. The **Table** section controls what learners see:\n\n" +
            "- **Search box** — lets learners filter the rows.\n" +
            "- **Pagination**, **Rows-per-page picker** and **Rows per page** — split long tables into pages.\n" +
            "- **Sort column** and **Sort order** — show the rows sorted by one column (**No sorting** keeps them in the order you typed them).\n" +
            "- **Theme** — **Basic**, **Striped**, **Border only in rows**, **Solid header** or **Thick border**.\n" +
            "- **Transparent background** — removes the table's background so the scene shows through.\n\n" +
            "The **Style** section adds opacity, padding and border width, style, color and radius.",
        },
        {
          id: "charts",
          title: "Creating a chart",
          videoUrl: null,
          text:
            "A new **Graphic** shows sample data as an area chart. To use your own data, select it and click **Plugin Settings** (the gear icon) in the icon strip on the right. The settings window has two steps:\n\n" +
            "- **Data source** — set **Num. Columns** and **Num. Rows**, then type the column headers and values. The first column usually holds the labels and the next columns the numbers. Click **Next step**.\n" +
            "- **Chart options** — pick the **Chart Type** (**Line**, **Area** or **Bar**), turn the horizontal and vertical grid lines on or off under **See grid**, choose which column goes on the **X Axis** and which column is plotted, and set the series name (**Graphic Name**) and **Color**. A **Preview** shows the result.\n\n" +
            "Click **Confirm changes** to update the chart. Its **Style** section in the right panel offers padding, border and opacity settings.",
        },
      ],
    },
    tr: {
      feature: "Bileşenler",
      title: "Tablolar ve Grafikler",
      sections: [
        {
          id: "overview",
          title: "Tablo veya grafik ekleme",
          videoUrl: null,
          text:
            "Sol paneldeki **Bileşenler → Öğeler**'i açın. **Veri** grubunda iki öğe bulunur — sahneye eklemek için birine tıklayın veya tuvale sürükleyin:\n\n" +
            "- **Tablo** — doğrudan tuval üzerinde yazdığınız bir veri tablosu.\n" +
            "- **Grafik** — küçük bir veri tablosundan oluşturulan çizgi, alan veya çubuk grafik.\n\n" +
            "Her ikisi de örnek verilerle başlar; böylece düzeni hemen görebilirsiniz.",
        },
        {
          id: "editing-tables",
          title: "Tabloyu düzenleme",
          videoUrl: null,
          text:
            "Yeni bir tablo doğrudan düzenleme modunda açılır. Daha sonra yeniden düzenlemek için tabloya **çift tıklayın**.\n\n" +
            "- Başlık satırı dahil herhangi bir hücreye tıklayıp yazın. **Enter** alttaki hücreye, **Shift+Enter** üstteki hücreye geçer.\n" +
            "- **Satır ekle** ve **Sütun ekle** tabloyu genişletir; her satırın ve sütunun yanındaki silme düğmeleri onu kaldırır (en az bir satır ve bir sütun her zaman kalır).\n" +
            "- **CSV içe aktar**, tabloyu bir CSV dosyasının içeriğiyle değiştirir. İlk satır başlık satırı olur; virgülle ve noktalı virgülle ayrılmış dosyalar (örneğin Excel çıktıları) desteklenir.\n" +
            "- Bitirmek için **Bitti**'ye tıklayın, **Esc**'ye basın veya tablonun dışına tıklayın. Değişiklikler diğer düzenlemeler gibi geri alınabilir.",
        },
        {
          id: "table-settings",
          title: "Tablo seçenekleri ve stil",
          videoUrl: null,
          text:
            "Tabloyu seçin ve sağ paneldeki **Ayarlar**'ı açın. **Tablo** bölümü, öğrencilerin ne göreceğini belirler:\n\n" +
            "- **Arama kutusu** — öğrencilerin satırları filtrelemesini sağlar.\n" +
            "- **Sayfalama**, **Satır sayısı seçicisi** ve **Sayfa başına satır** — uzun tabloları sayfalara böler.\n" +
            "- **Sıralama sütunu** ve **Sıralama yönü** — satırları bir sütuna göre sıralı gösterir (**Sıralama yok**, satırları yazdığınız sırada tutar).\n" +
            "- **Tema** — **Basit**, **Çizgili**, **Sadece satırlardaki çerçeve**, **Solid Başlık** veya **Kalın Çerçeve**.\n" +
            "- **Şeffaf arka plan** — tablonun arka planını kaldırır; sahne arkadan görünür.\n\n" +
            "**Stil** bölümü opaklık, iç boşluk ve çerçeve genişliği, stili, rengi ve köşe yuvarlaklığı ayarlarını sunar.",
        },
        {
          id: "charts",
          title: "Grafik oluşturma",
          videoUrl: null,
          text:
            "Yeni bir **Grafik**, örnek verileri alan grafiği olarak gösterir. Kendi verilerinizi kullanmak için grafiği seçin ve sağdaki simge şeridinde **Eklenti Ayarı**'na (dişli simgesi) tıklayın. Ayarlar penceresi iki adımdan oluşur:\n\n" +
            "- **Veri Kaynağı** — **Sütun Sayısı** ve **Satır Sayısı**'nı belirleyin, ardından sütun başlıklarını ve değerleri yazın. İlk sütun genellikle etiketleri, sonraki sütunlar sayıları içerir. **Sonraki Adım**'a tıklayın.\n" +
            "- **Grafik Seçenekleri** — **Grafik Türü**'nü seçin (**Çizgi**, **Alan** veya **Çubuk**), **Gridi Gör** altında yatay ve dikey ızgara çizgilerini açıp kapatın, **X Ekseni**'ne hangi sütunun geleceğini ve hangi sütunun çizileceğini seçin, serinin adını (**Grafik Adı**) ve **Renk**'ini belirleyin. **Ön izleme** sonucu gösterir.\n\n" +
            "Grafiği güncellemek için **Değişiklikleri Onayla**'ya tıklayın. Sağ paneldeki **Stil** bölümü iç boşluk, çerçeve ve opaklık ayarlarını sunar.",
        },
      ],
    },
    es: {
      feature: "Componentes",
      title: "Tablas y gráficos",
      sections: [
        {
          id: "overview",
          title: "Añadir una tabla o un gráfico",
          videoUrl: null,
          text:
            "Abre **Componentes → Elementos** en el panel izquierdo. El grupo **Datos** contiene dos elementos: haz clic en uno para añadirlo a la escena o arrástralo al lienzo:\n\n" +
            "- **Tabla**: una tabla de datos en la que escribes directamente sobre el lienzo.\n" +
            "- **Gráfico**: un gráfico de líneas, de áreas o de barras creado a partir de una pequeña tabla de datos.\n\n" +
            "Ambos empiezan con datos de ejemplo para que veas el diseño desde el primer momento.",
        },
        {
          id: "editing-tables",
          title: "Editar una tabla",
          videoUrl: null,
          text:
            "Una tabla nueva se abre directamente en modo de edición. Más adelante, haz **doble clic** en la tabla para volver a editarla.\n\n" +
            "- Haz clic en cualquier celda, incluida la fila de encabezado, y escribe. **Intro** pasa a la celda de abajo y **Mayús+Intro**, a la de arriba.\n" +
            "- **Añadir fila** y **Añadir columna** amplían la tabla; los botones de eliminar junto a cada fila y columna la quitan (siempre queda al menos una fila y una columna).\n" +
            "- **Importar CSV** sustituye la tabla por el contenido de un archivo CSV. La primera línea se convierte en la fila de encabezado; funcionan tanto los archivos separados por comas como por punto y coma (como las exportaciones de Excel).\n" +
            "- Haz clic en **Listo**, pulsa **Esc** o haz clic fuera de la tabla para terminar. Los cambios se pueden deshacer como cualquier otra edición.",
        },
        {
          id: "table-settings",
          title: "Opciones y estilo de la tabla",
          videoUrl: null,
          text:
            "Selecciona la tabla y abre **Ajustes** en el panel derecho. La sección **Tabla** controla lo que ven los alumnos:\n\n" +
            "- **Cuadro de búsqueda**: permite a los alumnos filtrar las filas.\n" +
            "- **Paginación**, **Selector de filas por página** y **Filas por página**: dividen las tablas largas en páginas.\n" +
            "- **Columna de orden** y **Orden**: muestran las filas ordenadas según una columna (**Sin ordenar** las mantiene en el orden en que las escribiste).\n" +
            "- **Tema**: **Básico**, **Alternado**, **Borde solo en filas**, **Cabecera sólida** o **Bordes gruesos**.\n" +
            "- **Fondo transparente**: quita el fondo de la tabla para que se vea la escena detrás.\n\n" +
            "La sección **Estilo** añade opacidad, relleno y el ancho, estilo, color y radio del borde.",
        },
        {
          id: "charts",
          title: "Crear un gráfico",
          videoUrl: null,
          text:
            "Un **Gráfico** nuevo muestra datos de ejemplo como gráfico de áreas. Para usar tus propios datos, selecciónalo y haz clic en **Ajustes del complemento** (el icono del engranaje) en la franja de iconos de la derecha. La ventana de configuración tiene dos pasos:\n\n" +
            "- **Fuente de datos**: define **Nº Columnas** y **Nº Filas** y, después, escribe los encabezados de columna y los valores. La primera columna suele contener las etiquetas y las siguientes, los números. Haz clic en **Siguiente paso**.\n" +
            "- **Opciones de la gráfica**: elige el **Tipo de gráfico** (**Línea**, **Área** o **Barras**), activa o desactiva las líneas de cuadrícula horizontales y verticales en **Ver rejilla**, elige qué columna va en el **Eje X** y qué columna se representa, y define el nombre de la serie (**Nombre Del Gráfico**) y el **Color**. La **Previsualización** muestra el resultado.\n\n" +
            "Haz clic en **Confirmar Cambios** para actualizar el gráfico. Su sección **Estilo** en el panel derecho ofrece ajustes de relleno, borde y opacidad.",
        },
      ],
    },
  },
};

export default tablesChartsGuide;
